/**
 * DOM-driven read-along highlight controller. Replaces the old
 * `useWordHighlightManager` render loop (document-wide `querySelectorAll` +
 * unconditional attribute rewrites every 50ms) with a change-only controller
 * that toggles `data-active` on exactly the elements the timing index says are
 * active, and only when the active set actually changes.
 *
 * Generic over the element type so the core is DOM-free and node-testable; the
 * web view instantiates it with `HTMLElement` + a `setActive` that writes
 * `dataset.active`.
 */

import {
  getChunkTimingIndex,
  type ChunkTimingSource,
} from '@listening/frontend/audio-chunk-player/timing-index';
import type { PlaybackSampleScheduler } from '@listening/frontend/audio-chunk-player/use-playback-derived';
import type {
  LegacySentenceKey,
  WordElementRegistry,
} from './word-element-registry';

export type HighlightController = {
  start: () => void;
  stop: () => void;
};

const isDocPackChunk = (chunk: ChunkTimingSource): boolean =>
  chunk.docPackWordTimings !== undefined && chunk.docPackChunk !== undefined;

export function createHighlightController<TEl>(opts: {
  registry: WordElementRegistry<TEl>;
  getChunk: () => ChunkTimingSource | null | undefined;
  getTtsId: () => string | undefined;
  getPositionMs: () => number;
  /** web: toggles `dataset.active`. */
  setActive: (el: TEl, active: boolean) => void;
  /** feeds the follow-adapter ref (the active sentence bbox element). */
  onAnchorElementChange: (el: TEl | null) => void;
  onActiveWordElementChange?: (el: TEl | null) => void;
  scheduler: PlaybackSampleScheduler;
}): HighlightController {
  const {
    registry,
    getChunk,
    getTtsId,
    getPositionMs,
    setActive,
    onAnchorElementChange,
    onActiveWordElementChange,
    scheduler,
  } = opts;

  let stopLoop: (() => void) | null = null;
  let lastGuard: string | null = null;
  let lastApplied = new Set<TEl>();
  let lastAnchor: TEl | null = null;
  let lastActiveWord: TEl | null = null;

  type ActiveDocPackTiming = {
    page: number;
    char_start: number;
    char_end: number;
  };
  type Descriptor =
    | { kind: 'none' }
    | { kind: 'dp'; timings: ReadonlyArray<ActiveDocPackTiming> }
    | {
        kind: 'lg';
        ttsId: string;
        activeIdx: number;
        timingCount: number | null;
      };

  // Cheap per-tick: only a binary search / small filter — no element resolution.
  const describe = (): Descriptor => {
    const chunk = getChunk();
    if (!chunk) return { kind: 'none' };
    const index = getChunkTimingIndex(chunk);
    const positionMs = getPositionMs();

    if (isDocPackChunk(chunk)) {
      const timings = index
        .activeDocPackTimingsAt(positionMs)
        .flatMap((t) =>
          t.page === null
            ? []
            : [
                {
                  page: t.page,
                  char_start: t.char_start,
                  char_end: t.char_end,
                },
              ],
        )
        .toSorted((a, b) => a.char_start - b.char_start);
      if (timings.length > 0) return { kind: 'dp', timings };
      const held = index.docPackTimingNear(positionMs);
      return {
        kind: 'dp',
        timings:
          held && held.page !== null
            ? [
                {
                  page: held.page,
                  char_start: held.char_start,
                  char_end: held.char_end,
                },
              ]
            : [],
      };
    }

    const ttsId = getTtsId();
    if (!ttsId) return { kind: 'none' };
    return {
      kind: 'lg',
      ttsId,
      activeIdx: Math.max(index.activeWordIdxAt(positionMs), 0),
      timingCount: index.legacyTimingCount,
    };
  };

  const keyOf = (d: Descriptor): string => {
    if (d.kind === 'none') return 'none';
    if (d.kind === 'dp')
      return `dp|${d.timings
        .map(
          (t) =>
            `${t.page.toString()}:${t.char_start.toString()}-${t.char_end.toString()}`,
        )
        .join(',')}`;
    return `lg|${d.ttsId}|${d.activeIdx.toString()}|${d.timingCount?.toString() ?? 'null'}`;
  };

  // Expensive (element resolution) — only run when the key changed.
  const resolve = (
    d: Descriptor,
  ): { els: Set<TEl>; anchor: TEl | null; activeWord: TEl | null } => {
    const els = new Set<TEl>();
    if (d.kind === 'none') return { els, anchor: null, activeWord: null };

    if (d.kind === 'dp') {
      let anchor: TEl | null = null;
      for (const t of d.timings) {
        const matched = registry.getDocPackWordEls(t.page, [
          t.char_start,
          t.char_end,
        ]);
        for (const el of matched) els.add(el);
        if (anchor === null && matched[0] !== undefined) anchor = matched[0];
      }
      return { els, anchor, activeWord: anchor };
    }

    // Legacy: the active word plus the untimed-word rule (idx >= alignment
    // count stay stamped while the chunk plays — parity with the old
    // `WordRendererChunkActive` no-timestamp path). Anchor = earliest active
    // word's sentence element.
    let anchorSentenceKey: LegacySentenceKey | undefined;
    let anchorWordIdx = Number.POSITIVE_INFINITY;
    let anchorWord: TEl | null = null;
    for (const word of registry.getLegacyChunkWords(d.ttsId)) {
      const timedActive =
        d.activeIdx >= 0 && word.wordIdxInChunk === d.activeIdx;
      const untimed =
        d.timingCount === null || word.wordIdxInChunk >= d.timingCount;
      if (!timedActive && !untimed) continue;
      for (const el of word.els) els.add(el);
      if (word.wordIdxInChunk < anchorWordIdx) {
        anchorWordIdx = word.wordIdxInChunk;
        anchorSentenceKey = word.sentenceKey;
        anchorWord = word.els[0] ?? null;
      }
    }
    const anchor = anchorSentenceKey
      ? (registry.getLegacySentenceEl(anchorSentenceKey) ?? null)
      : null;
    return { els, anchor, activeWord: anchorWord };
  };

  const tick = () => {
    const descriptor = describe();
    // `(activeKey, registry.version)` unchanged → zero DOM work + no element
    // resolution. version covers the virtualizer mounting the active word after
    // the position already settled.
    const guard = `${keyOf(descriptor)}|v${registry.version.toString()}`;
    if (guard === lastGuard) return;
    lastGuard = guard;

    const plan = resolve(descriptor);
    for (const el of lastApplied) if (!plan.els.has(el)) setActive(el, false);
    for (const el of plan.els) if (!lastApplied.has(el)) setActive(el, true);
    lastApplied = plan.els;

    if (plan.anchor !== lastAnchor) {
      lastAnchor = plan.anchor;
      onAnchorElementChange(plan.anchor);
    }
    if (plan.activeWord !== lastActiveWord) {
      lastActiveWord = plan.activeWord;
      onActiveWordElementChange?.(plan.activeWord);
    }
  };

  return {
    start: () => {
      if (stopLoop) return;
      tick(); // apply immediately, then loop
      stopLoop = scheduler.start(tick);
    },
    stop: () => {
      stopLoop?.();
      stopLoop = null;
      for (const el of lastApplied) setActive(el, false);
      lastApplied = new Set();
      lastGuard = null;
      if (lastAnchor !== null) {
        lastAnchor = null;
        onAnchorElementChange(null);
      }
      if (lastActiveWord !== null) {
        lastActiveWord = null;
        onActiveWordElementChange?.(null);
      }
    },
  };
}
